import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { useTextRotator } from "@/hooks/useTextRotator";
import { useCountUp } from "@/hooks/useCountUp";
import { Clock, TrendingUp, HeartHandshake, Star, Phone, Brain, CheckCircle2, MessageCircle, Key } from "lucide-react";
import heroImage from "@/assets/hero-hospitality.jpg";

interface StatCounterProps {
  end: number;
  suffix: string;
  label: string;
  icon: React.ElementType;
  delay: number;
  decimals?: number;
}

const StatCounter = ({ end, suffix, label, icon: Icon, delay, decimals = 0 }: StatCounterProps) => {
  const { ref, displayValue } = useCountUp({ end, suffix, decimals });
  
  return (
    <div 
      ref={ref}
      className="text-center opacity-0 animate-fade-up cursor-pointer transition-all duration-300 hover:scale-105 group rounded-xl p-2 sm:p-3 hover:bg-white/10 hover:backdrop-blur-sm"
      style={{ animationDelay: `${delay}ms`, animationFillMode: 'forwards' }}
    >
      <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400 mx-auto mb-1 group-hover:text-purple-300 group-hover:drop-shadow-[0_0_12px_rgba(168,85,247,0.8)] transition-all duration-300" />
      <div className="text-lg sm:text-xl md:text-2xl font-bold text-white group-hover:text-purple-50 transition-colors duration-300">{displayValue}</div>
      <div className="text-white/60 text-[10px] sm:text-xs group-hover:text-white/80 transition-colors duration-300">{label}</div>
    </div>
  );
};

// Floating UI Card Components
interface FloatingCardProps {
  t: (key: string) => string;
}

const IncomingCallCard = ({ t }: FloatingCardProps) => (
  <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-4 animate-fade-up" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
    <div className="flex items-center gap-3">
      <div className="relative">
        <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
          <Phone className="w-5 h-5 text-green-400" />
        </div>
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse" />
      </div>
      <div>
        <p className="text-white/60 text-xs">{t("hero.floatingCards.incomingCall")}</p>
        <p className="text-white font-medium text-sm">{t("hero.floatingCards.callerPhone")}</p>
      </div>
    </div>
  </div>
);

const IntentDetectionCard = ({ t }: FloatingCardProps) => (
  <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-4 animate-fade-up" style={{ animationDelay: '400ms', animationFillMode: 'forwards' }}>
    <div className="flex items-center gap-2 mb-3">
      <Brain className="w-4 h-4 text-purple-400" />
      <p className="text-white/60 text-xs">{t("hero.floatingCards.detectedIntent")}</p>
    </div>
    <div className="flex flex-wrap gap-2">
      <span className="px-3 py-1 rounded-full bg-purple-500/30 border border-purple-400/40 text-purple-200 text-xs font-medium">
        late_checkin
      </span>
      <span className="px-3 py-1 rounded-full bg-amber-500/30 border border-amber-400/40 text-amber-200 text-xs font-medium">
        door_code
      </span>
    </div>
  </div>
);

const ActionChipsCard = ({ t }: FloatingCardProps) => (
  <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-4 animate-fade-up" style={{ animationDelay: '600ms', animationFillMode: 'forwards' }}>
    <div className="flex flex-wrap gap-2">
      <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs font-medium transition-all duration-200">
        <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />
        {t("hero.floatingCards.verifyBooking")}
      </button>
      <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs font-medium transition-all duration-200">
        <Key className="w-3.5 h-3.5 text-amber-400" />
        {t("hero.floatingCards.sendDoorCode")}
      </button>
    </div>
  </div>
);

const AssistantMessageCard = ({ t }: FloatingCardProps) => (
  <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-4 animate-fade-up" style={{ animationDelay: '800ms', animationFillMode: 'forwards' }}>
    <div className="flex gap-3">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center flex-shrink-0">
        <MessageCircle className="w-4 h-4 text-white" />
      </div>
      <div>
        <p className="text-white text-sm leading-relaxed">
          "{t("hero.floatingCards.assistantMessage")}"
        </p>
        <p className="text-white/40 text-xs mt-1">{t("hero.floatingCards.justNow")}</p>
      </div>
    </div>
  </div>
);

const Hero = () => {
  const { t } = useTranslation();
  const [scrollY, setScrollY] = useState(0);
  
  const rotatingWords = t("hero.rotatingWords") as unknown as Array<{ word: string; article?: string }>;
  
  const { currentWord, currentArticle, isAnimating } = useTextRotator({
    words: rotatingWords,
    interval: 3000
  });

  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden">
      {/* Split Layout Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 w-full min-h-[90vh]">
        
        {/* Left Content Column - Gradient Background */}
        <div 
          className="relative flex items-center justify-center px-6 py-20 lg:py-0 lg:px-12 xl:px-20"
          style={{ 
            background: 'linear-gradient(135deg, #1a1a2e 0%, #2d1b4e 50%, #4a1c6b 100%)'
          }}
        >
          {/* Subtle overlay for depth */}
          <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-transparent to-purple-900/20" />
          
          <div className="relative max-w-xl z-10">
            {/* Platform Badge */}
            <div className="mb-6">
              <span className="inline-flex items-center bg-white/10 backdrop-blur-sm border border-white/20 text-white px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider">
                <span className="w-2 h-2 rounded-full bg-green-400 mr-2 animate-pulse" />
                {t("hero.platformBadge")}
              </span>
            </div>

            {/* Main Headline */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-6 leading-tight">
              <span className="block text-white">
                {t("hero.title")}{currentArticle && ` ${currentArticle}`}
              </span>
              <span className="block overflow-hidden h-[1.3em] leading-[1.3]">
                <span 
                  key={currentWord}
                  className={`inline-flex items-baseline text-transparent bg-clip-text bg-gradient-to-r from-purple-300 via-violet-400 to-fuchsia-400 ${isAnimating ? 'animate-roll-out' : 'animate-roll-in'}`}
                >
                  {currentWord}
                  <span className="inline-block w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-violet-400 ml-2 mb-1 shadow-lg shadow-violet-400/50" />
                </span>
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-base md:text-lg text-white/70 mb-8 max-w-lg leading-relaxed">
              {t("hero.subtitle.part1")}
              <span className="font-bold text-white">
                {t("hero.subtitle.highlight")}
              </span>
              {t("hero.subtitle.part2")}
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Link to="/demo">
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white border-0 shadow-lg shadow-purple-500/30 text-base px-8"
                >
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-400 mr-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-300 opacity-75"></span>
                  </span>
                  {t("hero.demo")}
                </Button>
              </Link>
              <Link to="/features">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="w-full sm:w-auto bg-white/5 border-white/30 text-white hover:bg-white/15 hover:border-white/50 backdrop-blur-md shadow-lg text-base px-8 transition-all duration-300"
                >
                  {t("hero.cta")}
                </Button>
              </Link>
            </div>

            {/* Stat Counters */}
            <div className="grid grid-cols-4 gap-1 sm:gap-3 pt-6 border-t border-white/20">
              <StatCounter end={40} suffix="+" label={t("hero.stats.hoursSaved")} icon={Clock} delay={0} />
              <StatCounter end={35} suffix="%" label={t("hero.stats.moreBookings")} icon={TrendingUp} delay={150} />
              <StatCounter end={24} suffix="/7" label={t("hero.stats.guestSupport")} icon={HeartHandshake} delay={300} />
              <StatCounter end={98} suffix="%" label={t("hero.stats.guestSatisfaction")} icon={Star} delay={450} />
            </div>
          </div>
        </div>

        {/* Right Image Column with Floating UI Cards */}
        <div className="relative hidden lg:block overflow-hidden">
          {/* Background Image with Parallax */}
          <div 
            className="absolute inset-0"
            style={{ transform: `translateY(${scrollY * 0.15}px)` }}
          >
            <img
              src={heroImage}
              alt={t("hero.heroImageAlt")}
              width={1920}
              height={1080}
              fetchPriority="high"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover scale-110"
            />

            {/* Bokeh/Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-[#4a1c6b]/80" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20" />
          </div>

          {/* Floating UI Cards */}
          <div className="relative h-full flex flex-col justify-center items-start p-8 xl:p-12 gap-4 z-10">
            <div className="ml-auto mr-8 w-64">
              <IncomingCallCard t={t} />
            </div>
            <div className="ml-12 w-56">
              <IntentDetectionCard t={t} />
            </div>
            <div className="ml-auto mr-16 w-64">
              <ActionChipsCard t={t} />
            </div>
            <div className="ml-8 w-80">
              <AssistantMessageCard t={t} />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile: Show image below on smaller screens */}
      <div className="lg:hidden absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a2e] via-[#2d1b4e] to-[#4a1c6b]" />
      </div>
    </section>
  );
};

export default Hero;
